/* jj-blocks.js — custom element definitions for the JJ product blocks.
   These live in an asset (not inline in the snippets) because Symmetry's
   quick-buy modal and the Custom Collection Slider quick view inject their
   HTML with innerHTML, and injected <script> tags never execute. Defining the
   elements globally means the blocks upgrade wherever they appear.
   The snippets keep their inline copies as a fallback; every define() is
   guarded, so whichever runs first wins and the other is a no-op. */

/* ---------- jj-shipping-calc ---------- */
(function () {
  if (!customElements.get('jj-shipping-calc')) {
    customElements.define('jj-shipping-calc', class extends HTMLElement {
      connectedCallback() {
        const out = this.querySelector('[data-delivery-window]');
        if (!out) return;
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const shipMax = parseInt(this.dataset.shipMax, 10) || 0;
        const tMin = parseInt(this.dataset.transitMin, 10) || 0;
        const tMax = parseInt(this.dataset.transitMax, 10) || 0;
        const cutoff = parseInt(this.dataset.cutoff, 10);
        const mode = this.dataset.weekendMode || 'none';
        const isSkipped = (d) => {
          const wd = d.getDay();
          if (mode === 'weekend') return wd === 0 || wd === 6;
          if (mode === 'sunday') return wd === 0;
          return false;
        };
        const now = new Date();
        let base = 0;
        if (!isNaN(cutoff) && cutoff < 24 && now.getHours() >= cutoff) base = 1;
        const mk = (offset) => {
          const d = new Date(now);
          d.setDate(d.getDate() + offset);
          while (isSkipped(d)) d.setDate(d.getDate() + 1);
          return d;
        };
        const fmt = (d) => days[d.getDay()] + ' ' + d.getDate() + ' ' + months[d.getMonth()];
        const from = mk(base + shipMax + tMin);
        const to = mk(base + shipMax + tMax);
        out.textContent = from.getTime() === to.getTime()
          ? fmt(from)
          : fmt(from) + ' \u2013 ' + fmt(to);
      }
    });
  }

})();

/* ---------- jj-mix-match ---------- */
(function () {
  if (!customElements.get('jj-mix-match')) {
    customElements.define('jj-mix-match', class extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute('data-selectable')) return;
        this.boxes = this.querySelectorAll('[data-mm-qty]');
        this.boxes.forEach((b) => {
          b.addEventListener('click', () => this.pick(b));
          b.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.pick(b); } });
        });
      }
      pick(box) {
        this.boxes.forEach((b) => b.setAttribute('aria-pressed', b === box ? 'true' : 'false'));
        const form = this.closest('product-info, .product-info, main, body').querySelector('form[action*="/cart/add"] [name="quantity"], form.js-product-form [name="quantity"]');
        if (form) {
          form.value = box.dataset.mmQty;
          form.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

})();

/* ---------- jj-variant-gate ---------- */
(function () {
  if (!customElements.get('jj-variant-gate')) {
    customElements.define('jj-variant-gate', class extends HTMLElement {
      connectedCallback() {
        // Defer so the theme's own variant scripts initialise first.
        setTimeout(() => this.init(), 0);
      }
      init() {
        window.jjGateDebug = () => (document.querySelector('jj-variant-gate') || {}).debug?.();
        this.scope = this.closest('.product-info, product-info, [data-product-info], section, main') || document;
        this.picker = this.scope.querySelector('variant-picker, .variant-picker, [data-variant-picker]');
        this.form = this.scope.querySelector('form[action*="/cart/add"]');
        this.btn = this.form && this.form.querySelector('[name="add"]');
        if (!this.form || !this.btn) return;
        if (!this.picker) { this.picker = this.form; }
        this.groups = this.optionGroups();
        this.requireProps = this.dataset.requireProps === 'true';
        if (!this.groups.length && !this.requireProps) return;
        this.touched = false;
        this.userChosen = new Set();
        // Mark a group as the customer's own choice when they interact with it.
        this.scope.addEventListener('click', (e) => {
          this.groups.forEach((g) => {
            const holder = this.groupField(g);
            if (holder && holder.contains(e.target)) this.userChosen.add(this.groupId(g));
          });
        }, true);
        this.lastValues = this.groups.map(() => '');
        const onPick = () => {
          if (this.suppressChange) { this.gate(); return; }
          this.touched = true;
          // Options depend on each other (a Samsung model has its own sizes), so
          // when one changes, everything chosen AFTER it is no longer valid.
          const current = this.groups.map((g) => this.valueOf(g));
          let changedAt = -1;
          for (let i = 0; i < this.groups.length; i += 1) {
            if (current[i] !== '' && current[i] !== this.lastValues[i]) { changedAt = i; break; }
          }
          if (changedAt > -1 && changedAt < this.groups.length - 1) {
            this.suppressChange = true;
            this.groups.slice(changedAt + 1).forEach((later) => {
              this.userChosen.delete(this.groupId(later));
              if (this.isChosen(later)) this.deselectGroup(later);
            });
            this.suppressChange = false;
          }
          this.enforceUntouched();
          this.lastValues = this.groups.map((g) => this.valueOf(g));
          this.gate();
        };
        // Bound to the scope so they survive the theme replacing the picker.
        this.scope.addEventListener('change', (e) => {
          if (!this.picker.contains(e.target) && !(e.target.closest && e.target.closest('custom-select'))) return;
          onPick();
        });
        this.scope.addEventListener('click', (e) => {
          const opt = e.target.closest && e.target.closest('.custom-select__option, label.opt-label, input.js-option');
          if (opt) setTimeout(onPick, 0);
        }, true);
        // The button stays enabled so a click can explain what is missing.
        // While the gate is CLOSED we stop the click completely (capture phase):
        // option apps such as Easify add to cart from their own click handler,
        // so preventDefault alone would not stop them.
        // While the gate is OPEN we do nothing at all, leaving those handlers
        // free to sync their values and complete the add.
        this.btn.addEventListener('click', (e) => {
          this.gate();
          if (!this.btn.hasAttribute('data-jj-gated')) return;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.promptsShown = true;
          this.prompts(true);
          const first = this.picker.querySelector('.jj-gate-prompt');
          const holder = first && first.closest('.option-selector, fieldset');
          if (holder) holder.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, true);
        // Last line of defence: block the actual submit while incomplete.
        this.form.addEventListener('submit', (e) => {
          if (!this.btn.hasAttribute('data-jj-gated')) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          this.promptsShown = true;
          this.prompts(true);
        }, true);
        if (this.requireProps) {
          // App option fields (e.g. Easify) render their own widgets — watch all
          // text-like inputs in the product area, not just properties[] names.
          const onEdit = (e) => {
            const t = e.target;
            if (t && (t.matches('input, textarea'))) { this.touched = true; this.gate(); }
          };
          this.scope.addEventListener('input', onEdit);
          this.scope.addEventListener('change', onEdit);
        }
        this.deselectAll();
        this.gate();

        // The theme re-renders / re-resolves the picker after a choice (picking
        // a model makes it select a size too). React to the DOM settling rather
        // than to a guessed delay: whenever something in the picker changes,
        // blank every group the customer has not chosen themselves.
        this.watchdog = new MutationObserver(() => {
          if (this.suppressChange || this.enforcing) return;
          clearTimeout(this._enforceT);
          this._enforceT = setTimeout(() => {
            this.enforcing = true;
            const live = this.scope.querySelector('variant-picker, .variant-picker, [data-variant-picker]');
            if (live && live !== this.picker) {      // picker element was replaced
              this.picker = live;
              this.groups = this.optionGroups();
            }
            this.enforceUntouched();
            this.lastValues = this.groups.map((g) => this.valueOf(g));
            this.gate();
            this.enforcing = false;
          }, 60);
        });
        this.watchdog.observe(this.scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-selected', 'checked', 'class'] });
        // Symmetry's own scripts may re-render selection state and the button
        // after us — re-assert until the customer interacts.
        [120, 350, 800, 1600].forEach((ms) => setTimeout(() => {
          if (!this.touched) {
            if (this.groups.some((g) => this.isChosen(g))) this.deselectAll();
            this.gate();
          }
        }, ms));
      }
      optionGroups() {
        const inputs = this.picker.querySelectorAll('input[type="radio"]');
        const names = new Set();
        inputs.forEach((i) => names.add(i.name));
        const radio = Array.from(names).map((n) => ({ type: 'radio', name: n }));
        const selects = Array.from(this.picker.querySelectorAll('select')).map((el) => ({ type: 'select', el }));
        // Dropdown pickers render <custom-select> with NO native <select>.
        const customs = Array.from(this.picker.querySelectorAll('custom-select')).map((el) => ({ type: 'custom', el }));
        return radio.concat(selects, customs);
      }
      isChosen(g) {
        if (g.type === 'radio') return !!this.picker.querySelector('input[name="' + CSS.escape(g.name) + '"]:checked');
        if (g.type === 'select') return g.el.value !== '' && g.el.selectedIndex >= 0;
        // <custom-select> can keep its original option marked as selected even
        // after we blank the visible choice, so trust what the customer SEES:
        // the button text must match the option that claims to be selected.
        const opt = g.el.querySelector('[role="option"][aria-selected="true"]');
        const val = opt ? (opt.dataset.value || '') : '';
        if (!val) return false;
        const btnEl = g.el.querySelector('.custom-select__btn');
        const btnTxt = btnEl ? (btnEl.textContent || '').trim() : '';
        const norm = (x) => (x || '').toLowerCase().replace(/\s+/g, ' ').trim();
        if (btnTxt && norm(btnTxt) !== norm(val)) return false;
        return true;
      }
      groupField(g) {
        if (g.type === 'radio') {
          const input = this.picker.querySelector('input[name="' + CSS.escape(g.name) + '"]');
          return input ? input.closest('.option-selector, fieldset') : null;
        }
        return g.el.closest('.option-selector, fieldset');
      }
      deselectGroup(g) {
        if (g.type === 'radio') {
          this.picker.querySelectorAll('input[name="' + CSS.escape(g.name) + '"]').forEach((i) => { i.checked = false; });
          return;
        }
        if (g.type === 'select') {
          if (!g.el.querySelector('option[value=""]')) {
            const ph = document.createElement('option');
            ph.value = '';
            ph.textContent = this.dataset.prompt || 'Choose an option';
            g.el.insertBefore(ph, g.el.firstChild);
          }
          g.el.value = '';
          return;
        }
        const cs = g.el;
        try {
          const listbox = cs.querySelector('[role="listbox"], ul');
          if (!listbox) return;
          let ph = listbox.querySelector('[data-value=""]');
          if (!ph) {
            ph = document.createElement('li');
            ph.className = 'custom-select__option js-option jj-gate-placeholder';
            ph.id = (cs.id || 'jj-cs-' + Math.random().toString(36).slice(2)) + '-jj-ph';
            ph.setAttribute('role', 'option');
            ph.setAttribute('aria-selected', 'false');
            ph.dataset.value = '';
            const span = document.createElement('span');
            span.textContent = this.dataset.prompt || 'Choose an option';
            ph.appendChild(span);
            listbox.insertBefore(ph, listbox.firstElementChild);
          }
          if (typeof cs.selectOption === 'function') cs.selectOption(ph);
          const btn = cs.querySelector('.custom-select__btn');
          if (btn && btn.firstElementChild) btn.firstElementChild.textContent = this.dataset.prompt || 'Choose an option';
        } catch (err) { /* leave the picker alone if the theme changes */ }
      }
      deselectAll() {
        this.suppressChange = true;
        this.groups.forEach((g) => this.deselectGroup(g));
        this.suppressChange = false;
      }
      // After the theme re-resolves a variant (picking one option can make it
      // select the others), blank every group the customer has NOT chosen.
      enforceUntouched() {
        this.suppressChange = true;
        this.groups.forEach((g) => {
          if (!this.userChosen.has(this.groupId(g)) && this.isChosen(g)) this.deselectGroup(g);
        });
        this.suppressChange = false;
      }
      valueOf(g) {
        if (!this.isChosen(g)) return '';
        if (g.type === 'radio') {
          const el = this.picker.querySelector('input[name="' + CSS.escape(g.name) + '"]:checked');
          return el ? el.value : '';
        }
        if (g.type === 'select') return g.el.value || '';
        const btn = g.el.querySelector('.custom-select__btn');
        return btn ? (btn.textContent || '').trim() : '';
      }
      groupId(g) {
        // Element ids change when the theme re-renders the picker, so identify a
        // group by its option NAME instead — that is stable.
        const holder = this.groupField(g);
        let label = '';
        if (holder) {
          label = holder.getAttribute('data-option') || '';
          if (!label) {
            const l = holder.querySelector('legend, .label');
            label = l ? l.textContent : '';
          }
        }
        label = (label || '').replace(/\s+/g, ' ').trim().toLowerCase();
        if (label) return 'opt:' + label;
        return g.type === 'radio' ? 'radio:' + g.name : 'idx:' + this.groups.indexOf(g);
      }
      debug() {
        const fields = this.appFields();
        const selects = Array.from(this.picker.querySelectorAll('select'));
        const customs = Array.from(this.picker.querySelectorAll('custom-select'));
        const scopeCustoms = Array.from((this.scope || document).querySelectorAll('custom-select'));
        return {
          requireProps: this.requireProps,
          optionGroups: this.groups.length,
          appFieldsFound: fields.length,
          appFields: fields.map((f) => ({ name: f.name || '(no name)', placeholder: f.placeholder || '', value: f.value })),
          complete: this.complete(),
          pickerTag: this.picker && this.picker.tagName,
          pickerClass: this.picker && this.picker.className,
          radiosInPicker: this.picker.querySelectorAll('input[type="radio"]').length,
          selectsInPicker: selects.length,
          selectValues: selects.map((el) => ({ name: el.name || '(no name)', value: el.value, options: el.options.length })),
          customSelectsInPicker: customs.length,
          customSelectsInScope: scopeCustoms.length,
          customSelectHasApi: customs.map((c) => typeof c.selectOption === 'function'),
          customSelectButtonText: scopeCustoms.map((c) => {
            const b = c.querySelector('.custom-select__btn');
            return b ? b.textContent.trim().slice(0, 40) : '(no button)';
          })
        };
      }
      complete() {
        const optionsDone = this.groups.every((g) => this.isChosen(g));
        if (!optionsDone) return false;
        if (!this.requireProps) return true;
        return this.appFields().every((f) => f.value.trim() !== '');
      }
      appFields() {
        // Easify (and similar apps) can render their inputs inside the product
        // form, next to it, or in their own wrapper. Search all of those, then
        // filter down to fields a customer is actually expected to fill.
        const sel = 'input[type="text"], input[type="number"], input[type="tel"], input[type="email"], input:not([type]), textarea, select';
        const roots = [];
        if (this.form) roots.push(this.form);
        if (this.scope && this.scope !== this.form) roots.push(this.scope);
        document.querySelectorAll('[class*="easify"], [id*="easify"], [class*="product-option"], [data-product-options]').forEach((el) => roots.push(el));
        const seen = new Set();
        const found = [];
        roots.forEach((root) => {
          root.querySelectorAll(sel).forEach((f) => {
            if (seen.has(f)) return;
            seen.add(f);
            if (f.offsetParent === null) return;                       // hidden
            if (f.disabled || f.readOnly) return;
            if (f.name === 'quantity' || f.classList.contains('qty-input__input')) return;
            if (f.closest('variant-picker, .variant-picker, .option-selector, quantity-input, .quantity-input, .qty-input')) return;
            if (f.closest('jj-shipping-calc, jj-mix-match, jj-recently-viewed, .jj-qv, form[action*="/search"]')) return;
            // Size-guide popups carry their own inputs (wrist calculator, model
            // search). They are never part of the order, so they must not gate it.
            if (f.closest('[id$="modal-overlay"], .jj-ap-overlay, .jj-sg2-overlay, .jj-fb-overlay, .jj-ob-overlay, .jj-ap-modal, .jj-sg2-modal, .jj-fb-modal, .jj-ob-modal, dialog, .modal')) return;
            if (f.type === 'search' || f.name === 'q' || /zoek|search/i.test(f.placeholder || '')) return;
            // Ignore anything with no on-screen size (hidden panels, collapsed UI).
            const r = f.getBoundingClientRect();
            if (r.width === 0 || r.height === 0) return;
            if (f.type === 'checkbox' || f.type === 'radio' || f.type === 'hidden') return;
            found.push(f);
          });
        });
        return found;
      }
      prompts(show) {
        const text = this.dataset.prompt || '';
        this.groups.forEach((g) => {
          const chosen = this.isChosen(g);
          const field = this.groupField(g);
          if (!field) return;
          const legend = field.querySelector('legend, .label');
          if (!legend) return;
          let tag = legend.querySelector('.jj-gate-prompt');
          if (show && !chosen) {
            if (!tag) {
              tag = document.createElement('span');
              tag.className = 'jj-gate-prompt';
              tag.textContent = text;
              legend.appendChild(tag);
            }
          } else if (tag) {
            tag.remove();
          }
        });
      }
      gate() {
        const done = this.complete();
        this.btn.classList.toggle('jj-gated', !done);
        this.btn.setAttribute('aria-disabled', done ? 'false' : 'true');
        if (done) {
          this.btn.removeAttribute('data-jj-gated');
          this.prompts(false);
        } else {
          this.btn.setAttribute('data-jj-gated', '');
          if (this.promptsShown) this.prompts(true);
        }
      }
    });
  }

})();

/* ---------- size-guide bridge ----------
   The native size-chart link opens the JJ custom size-guide popup whenever one
   is present in the document. Lives here (not inline in product-popups.liquid)
   so it also works inside Symmetry's quick-buy modal and the JJ quick view,
   whose markup is injected with innerHTML (injected scripts never run).
   Without a JJ popup present, the theme's own modal behaves as normal. */
(function () {
  function jjOverlay(scope) {
    var sel = '#jj-ap-modal-overlay, #jj-sg2-modal-overlay, #jj-fb-modal-overlay, #jj-ob-modal-overlay';
    return (scope && scope.querySelector(sel)) || document.querySelector(sel);
  }
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.size-chart-link, [data-qv-sizeguide]');
    if (!link) return;
    // Prefer a popup inside the same modal/card, else anywhere on the page.
    var scope = link.closest('.jj-qv, .modal, .quickbuy, [data-name="quickbuy"], .product-info') || document;
    var ov = jjOverlay(scope);
    if (!ov) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    ov.classList.add('open');
  }, true);
})();

/* ---------- Shop op steen: A-Z filter ----------
   Delegated so it works after customizer re-renders. Clicking a letter shows
   only stones starting with it; clicking the same letter again clears the
   filter and shows every stone (the entry state). */
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-jj-letter]');
    if (!btn || btn.disabled) return;
    var root = btn.closest('[data-jj-stone-index]');
    if (!root) return;
    var letter = btn.dataset.jjLetter;
    var active = btn.getAttribute('aria-pressed') === 'true';
    var next = active ? null : letter;

    root.querySelectorAll('[data-jj-letter]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.dataset.jjLetter === next));
    });

    var shown = 0;
    root.querySelectorAll('.jj-si__row').forEach(function (row) {
      var match = !next || row.dataset.letter === next;
      row.hidden = !match;
      if (!match) row.open = false;
      if (match) shown += 1;
    });
    var empty = root.querySelector('[data-jj-stone-empty]');
    if (empty) empty.hidden = shown > 0;
  });
})();

/* ---------- cart drawer quick-add ----------
   Adds a single-variant recommendation straight to the cart from inside the
   drawer, so the customer never has to leave it. Multi-variant and sold-out
   products render as a plain link instead (markup: product-block.liquid,
   behind the jj_quick_add parameter) and never reach this handler.

   Delegated from document because the automatic recommendations are fetched
   from the mini-related-products section and injected with innerHTML — an
   inline script would never execute there.

   It deliberately reuses the theme's own add request: the Section Rendering
   API refreshes page-header + cart-drawer in place, exactly as the normal
   add-to-cart button does, so the drawer updates without a reload.

   It dispatches on:cart:change but NOT on:cart:add. The customer is already
   looking at the drawer, so the cart pop-up would cover the very thing that
   just updated — and the pop-up's drawer suppression would fight the drawer
   the customer is using. on:cart:change keeps the pop-up's snapshot in sync
   without announcing anything. */
(function () {
  var busy = false;

  function sectionTargets() {
    return ['page-header', 'cart-drawer']
      .map(function (sel) { return document.querySelector(sel); })
      .filter(function (el) { return el && el.dataset && el.dataset.sectionId; });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-jj-quickadd]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (busy || btn.classList.contains('is-busy')) return;

    var variantId = btn.dataset.jjQuickadd;
    if (!variantId) return;
    busy = true;
    btn.classList.add('is-busy');

    var targets = sectionTargets();
    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);
    body.append('sections_url', window.location.pathname);
    body.append('sections', targets.map(function (el) { return el.dataset.sectionId; }).join(','));

    var addUrl = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add';

    fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        var data = res.data;
        // Refresh the drawer in place when the sections came back intact;
        // otherwise let the theme rebuild from the change event.
        if (data.sections && targets.length && Object.keys(data.sections).length === targets.length) {
          targets.forEach(function (el) {
            if (typeof el.updateFromCartChange === 'function') {
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
            }
          });
        }
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        // The drawer markup above was just replaced, so this button may be
        // gone. Only touch it if it is still connected to the document.
        if (btn.isConnected) {
          btn.classList.remove('is-busy');
          btn.classList.add('is-done');
          setTimeout(function () { if (btn.isConnected) btn.classList.remove('is-done'); }, 1200);
        }
      })
      .catch(function () {
        if (btn.isConnected) btn.classList.remove('is-busy');
        // Never leave the customer stuck: fall back to the product page.
        var card = btn.closest('.product-block, li');
        var link = card && card.querySelector('a.product-link[href]');
        if (link) window.location.href = link.getAttribute('href');
      })
      .finally(function () { busy = false; });
  }, true);
})();

/* Diagnostic: jjQuickAddDebug() — reports every quick-add icon currently in the
   drawer, whether it adds directly or links out, and the variant count that
   decided it. If a product you expect to add directly shows mode 'link', read
   its variants value: >1 means Shopify really does have several variants for
   it (a named option with one value still counts as one variant, so 1 here
   with mode 'link' would mean the product is unavailable). */
window.jjQuickAddDebug = function () {
  var rows = Array.prototype.map.call(document.querySelectorAll('.jj-qa'), function (el) {
    return {
      title: el.dataset.jjQaTitle || '(unknown)',
      mode: el.hasAttribute('data-jj-quickadd') ? 'direct add' : 'link',
      variants: Number(el.dataset.jjQaVariants || 0),
      variantId: el.dataset.jjQuickadd || null
    };
  });
  if (console && console.table) console.table(rows);
  return { icons: rows.length, direct: rows.filter(function (r) { return r.mode === 'direct add'; }).length, rows: rows };
};

/* ---------- stone filter bar (jj-stone-filter-bar) ----------
   Separate from the [data-jj-stone-index] handler above: that one opens/closes
   <details> rows on the Shop-by-stone page, this one filters chips that link
   out to each stone's collection. Kept apart on purpose so a change to one
   cannot alter the other.

   Delegated from document so it also works in the customizer preview, which
   injects section markup and never runs inline scripts. */
(function () {
  function apply(root, letter) {
    var shown = 0;
    root.querySelectorAll('.jj-sf__chip[data-letter]').forEach(function (chip) {
      var match = !letter || chip.dataset.letter === letter;
      chip.hidden = !match;
      if (match) shown += 1;
    });
    root.querySelectorAll('[data-jj-sf-letter]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.dataset.jjSfLetter === letter));
    });
    var empty = root.querySelector('[data-jj-sf-empty]');
    if (empty) empty.hidden = shown > 0;
  }

  // On a stone collection page, pre-select that stone's letter so the row lists
  // only its alphabetical neighbours. Also brings the active chip into view
  // without scrolling the page itself.
  function init(root) {
    if (root.dataset.sfInit) return;
    root.dataset.sfInit = '1';
    var active = root.querySelector('.jj-sf__chip.is-active[data-letter]');
    var autoLetter = root.dataset.autoLetter === 'true';

    if (active && autoLetter) {
      apply(root, active.dataset.letter);
    } else if (root.dataset.startFiltered === 'true') {
      root.querySelectorAll('.jj-sf__chip[data-letter]').forEach(function (chip) { chip.hidden = true; });
      var empty = root.querySelector('[data-jj-sf-empty]');
      if (empty) empty.hidden = true;
    }

    if (active) {
      var strip = root.querySelector('[data-jj-sf-chips]');
      if (strip && strip.scrollWidth > strip.clientWidth) {
        strip.scrollLeft = Math.max(0, active.offsetLeft - (strip.clientWidth / 2) + (active.offsetWidth / 2));
      }
    }
  }

  function initAll() {
    document.querySelectorAll('[data-jj-stone-filter]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-jj-sf-letter]');
    if (!btn || btn.disabled) return;
    var root = btn.closest('[data-jj-stone-filter]');
    if (!root) return;
    var active = btn.getAttribute('aria-pressed') === 'true';
    apply(root, active ? null : btn.dataset.jjSfLetter);
  });

  /* Diagnostic: jjStoneBarDebug() — chips found, which letters are usable,
     which chip matched the current collection, and the active filter. */
  window.jjStoneBarDebug = function () {
    var root = document.querySelector('[data-jj-stone-filter]');
    if (!root) return 'no stone filter bar on this page';
    var chips = Array.prototype.slice.call(root.querySelectorAll('.jj-sf__chip[data-letter]'));
    var pressed = root.querySelector('[data-jj-sf-letter][aria-pressed="true"]');
    var act = root.querySelector('.jj-sf__chip.is-active[data-letter]');
    return {
      chips: chips.length,
      visible: chips.filter(function (c) { return !c.hidden; }).length,
      activeLetter: pressed ? pressed.dataset.jjSfLetter : null,
      matchedCurrentCollection: act ? act.dataset.handle : null,
      startFiltered: root.dataset.startFiltered === 'true',
      lettersEnabled: Array.prototype.slice.call(root.querySelectorAll('[data-jj-sf-letter]:not([disabled])'))
        .map(function (b) { return b.dataset.jjSfLetter; })
    };
  };
})();
/* ---------- size-guide popup close (duplicate-ID safe) ----------
   The popups' own × buttons close via getElementById in an inline onclick.
   When the SAME popup exists twice on a page — once at page level (the
   custom-liquid block in main-product) and once inside an injected JJ quick
   view — getElementById resolves to the FIRST copy, so the visible second
   copy never closes. This delegated handler closes the overlay that actually
   CONTAINS the clicked button, which is correct however many copies exist.
   The inline onclick still fires afterwards against the first copy; that is a
   harmless no-op. Escape closes every open copy for the same reason. */
(function () {
  var OVERLAYS = '#jj-ap-modal-overlay, #jj-sg2-modal-overlay, #jj-fb-modal-overlay, #jj-ob-modal-overlay, .jj-ap-overlay, .jj-sg2-overlay, .jj-fb-overlay, .jj-ob-overlay';
  document.addEventListener('click', function (e) {
    var closer = e.target.closest('.jj-ap-close, .jj-sg2-close, .jj-fb-close, .jj-ob-close');
    if (closer) {
      var ov = closer.closest(OVERLAYS);
      if (ov) ov.classList.remove('open');
      return;
    }
    // Backdrop click: only when the overlay itself (not the modal card) is hit.
    var direct = e.target;
    if (direct.matches && direct.matches(OVERLAYS) && direct.classList.contains('open')) {
      direct.classList.remove('open');
    }
  }, true);
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll(OVERLAYS).forEach(function (ov) {
      ov.classList.remove('open');
    });
  });
})();

/* ---------- custom entry fields (Easify replacement) ----------
   Runtime for snippets/jj-custom-fields.liquid: the character counter, the
   minimum-length enforcement on the add-to-cart click, and the automatic
   custom-fit fee. Delegated from document because the fields render in three
   injected contexts (PDP, Symmetry quick-buy modal, JJ quick view).

   Emptiness gating is NOT done here: on the PDP the variant gate's
   "Also require app option fields" already blocks ATC while a field is empty
   (the wrapper carries data-product-options, which the gate scans), and in
   the quick view the sections' own syncVariant does the same. This handler
   only adds what neither covers: minimum lengths.

   The fee: when the wrapper carries data-jj-cf-fee (a variant id from the
   Theme settings fee product), a successful add of the associated product
   adds one unit of the fee product with a property naming what it is for.
   The property key starts with an underscore, so it is hidden from the
   customer-facing cart but visible in the order details. */
(function () {
  // character counter
  document.addEventListener('input', function (e) {
    var f = e.target.closest('[data-jj-cf-input]');
    if (!f) return;
    var wrapEl = f.closest('.jj-cf__inputwrap');
    var count = wrapEl && wrapEl.querySelector('[data-jj-cf-count]');
    if (count) {
      var max = f.getAttribute('maxlength');
      count.textContent = f.value.length + (max ? '/' + max : '');
    }
    // typing again clears the too-short state until the next ATC attempt
    var field = f.closest('.jj-cf__field');
    var warn = field && field.querySelector('[data-jj-cf-warn]');
    f.removeAttribute('data-jj-cf-invalid');
    if (warn) warn.hidden = true;
  });

  function formOf(btn) {
    if (btn.form) return btn.form;
    return btn.closest('form');
  }
  function cfFieldsFor(form) {
    if (!form) return [];
    // input.form resolves the form="" attribute, so PDP fields outside the
    // form element still associate correctly.
    return Array.prototype.filter.call(document.querySelectorAll('[data-jj-cf-input]'), function (f) {
      return f.form === form && f.offsetParent !== null;
    });
  }

  /* Required + minimum-length enforcement at the last possible moment: the
     ATC click. The inputs deliberately carry NO native required attribute —
     the browser's constraint bubble renders in the browser's own UI language
     ("Please fill in this field."), not the shop language. These warnings use
     the texts from Theme settings instead. Runs in every context (PDP,
     quick-buy modal, quick view) as a safety net behind the gates. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('button[name="add"], [data-qv-atc]');
    if (!btn || btn.disabled) return;
    var form = formOf(btn);
    var bad = null;
    var kind = null;
    cfFieldsFor(form).forEach(function (f) {
      if (bad) return;
      var v = (f.value || '').trim();
      var min = parseInt(f.dataset.jjCfMin || '0', 10);
      if (f.dataset.jjCfRequired === 'true' && v === '') { bad = f; kind = 'req'; return; }
      if (min > 0 && v !== '' && v.length < min) { bad = f; kind = 'min'; }
    });
    if (!bad) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    bad.setAttribute('data-jj-cf-invalid', '');
    var field = bad.closest('.jj-cf__field');
    var warn = field && field.querySelector('[data-jj-cf-warn]');
    if (warn) {
      warn.textContent = (kind === 'req' ? warn.dataset.warnReq : warn.dataset.warnMin) || warn.dataset.warnMin || '';
      warn.hidden = false;
    }
    bad.focus();
  }, true);

  /* ---------- custom-fit fee: keep it in step with the band ----------
     The fee is its own cart line, so a customer could simply remove it and
     keep the made-to-measure band. Hiding the remove link (see cart-item)
     stops the obvious route; this keeps the cart CORRECT whatever happens,
     including cases the UI cannot cover: quantity changed on the band, the
     fee removed via the cart page, an old cart restored from a previous
     session, or the band removed while the fee lingers.

     After every cart change we compare what the cart SHOULD hold with what it
     does, and fix the difference:
       required fee quantity = total quantity of made-to-measure band lines
     A made-to-measure line is one carrying the custom entry-field properties
     (they are line item properties, so they travel with the cart).

     Note this is a correction, not a lock: the cart API is public, so nothing
     client-side can be tamper-proof. Shopify Functions (cart transform) is the
     only server-side guarantee. In practice this covers the realistic cases —
     and it also fixes the fee silently going wrong when quantities change,
     which the original add-once handler never did. */
  var feeBusy = false;
  var feeLastFix = 0;
  function cfConfig() {
    var host = document.querySelector('[data-jj-cf-fee]');
    if (host && host.dataset.jjCfFee) {
      return { feeId: String(host.dataset.jjCfFee), labels: cfLabels() };
    }
    var cfg = document.querySelector('[data-jj-cf-config]');
    if (cfg && cfg.dataset.feeVariant) {
      return { feeId: String(cfg.dataset.feeVariant), labels: (cfg.dataset.feeTriggers || '').split('|').filter(Boolean) };
    }
    return null;
  }
  function cfLabels() {
    // the customer-facing field labels double as the property keys
    return Array.prototype.map.call(document.querySelectorAll('[data-jj-cf-input]'), function (f) {
      var m = (f.name || '').match(/^properties\[(.+)\]$/);
      return m ? m[1] : null;
    }).filter(Boolean);
  }
  function isMadeToMeasure(item, labels) {
    if (!item.properties) return false;
    for (var k = 0; k < labels.length; k += 1) {
      var v = item.properties[labels[k]];
      if (v !== undefined && v !== null && String(v).trim() !== '') return true;
    }
    return false;
  }
  function reconcileFee() {
    if (feeBusy) return;
    if (Date.now() - feeLastFix < 1200) return; // just corrected; let it settle
    var cfg = cfConfig();
    if (!cfg || !cfg.feeId || !cfg.labels.length) return;
    feeBusy = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var needed = 0;
        var feeLine = null;
        var feeIndex = 0;
        (cart.items || []).forEach(function (item, i) {
          if (String(item.variant_id) === cfg.feeId) { feeLine = item; feeIndex = i + 1; return; }
          if (isMadeToMeasure(item, cfg.labels)) needed += item.quantity;
        });
        var have = feeLine ? feeLine.quantity : 0;
        if (needed === have) return null;
        if (feeLine) {
          // line index is stable for change.js and survives duplicate variants
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: feeIndex, quantity: needed })
          });
        }
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: Number(cfg.feeId),
            quantity: needed,
            properties: { '_Surcharge for': 'Made-to-measure order' }
          })
        });
      })
      .then(function (res) {
        if (!res) return;
        /* We dispatch on:cart:change below, which re-enters this function.
           That second pass finds nothing to do and stops — but throttle
           anyway so a mis-set fee product can never cause a request loop. */
        feeLastFix = Date.now();
        // tell the theme to redraw the drawer / cart with the corrected lines
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      })
      .catch(function () {})
      .finally(function () { feeBusy = false; });
  }
  // after an add, and after any change (quantity edit, line removal)
  document.addEventListener('on:cart:add', function () { setTimeout(reconcileFee, 60); });
  document.addEventListener('on:cart:change', function () { setTimeout(reconcileFee, 60); });
  // and on load, so a cart restored from a previous session is corrected too
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(reconcileFee, 300); });
  } else {
    setTimeout(reconcileFee, 300);
  }
  window.jjFeeReconcile = reconcileFee;

  /* Diagnostic: jjCfDebug() — fields found, which form they belong to,
     min/max state, fee wiring. */
  window.jjCfDebug = function () {
    var host = document.querySelector('[data-jj-cf]');
    if (!host) return 'no custom fields on this page';
    return {
      fee: host.dataset.jjCfFee || null,
      fields: Array.prototype.map.call(host.querySelectorAll('[data-jj-cf-input]'), function (f) {
        return {
          name: f.name,
          value: f.value,
          min: f.dataset.jjCfMin || null,
          max: f.getAttribute('maxlength'),
          required: f.required,
          inForm: !!f.form,
          visible: f.offsetParent !== null
        };
      })
    };
  };
})();

/* ---------- quick view gate feedback ----------
   The QV add-to-cart is "gated" rather than disabled (a disabled button fires
   no click, so a customer who had missed something got a dead button and no
   explanation). Clicking while gated blocks the add and shows the reason —
   which option or which fields are still missing — under the button. */
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qv-atc]');
    if (!btn || !btn.hasAttribute('data-qv-gated')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var scope = btn.closest('[data-qv-panel]') || btn.closest('.jj-qv');
    var msg = scope && scope.querySelector('[data-qv-gatemsg]');
    var reason = btn.dataset.gateReason || 'option';
    if (reason === 'option') {
      /* Point at the option itself rather than writing a sentence under the
         button — same behaviour as the product page's variant gate. Every
         group still missing a choice gets the prompt beside its name. */
      if (msg) msg.hidden = true;
      if (scope) {
        scope.querySelectorAll('[data-qv-optgroup]').forEach(function (g) {
          var prompt = g.querySelector('[data-qv-optprompt]');
          if (!prompt) return;
          var ddl = g.querySelector('[data-qv-select]');
          var chosen = ddl ? ddl.value !== '' : !!g.querySelector('[data-qv-opt][aria-pressed="true"]');
          prompt.hidden = chosen;
        });
      }
    } else if (msg) {
      msg.textContent = reason === 'fields'
        ? (btn.dataset.msgFields || 'Please fill in all the fields above first.')
        : (btn.dataset.soldLabel || 'Sold out');
      msg.hidden = false;
    }
    // point the customer at the first thing that actually needs attention
    if (scope) {
      var target = null;
      if (reason === 'fields') {
        var fields = scope.querySelectorAll('[data-jj-cf-input]');
        for (var i = 0; i < fields.length; i += 1) {
          var f = fields[i];
          var v = (f.value || '').trim();
          var min = parseInt(f.dataset.jjCfMin || '0', 10);
          if ((f.dataset.jjCfRequired === 'true' && v === '') || (v !== '' && min > 0 && v.length < min)) { target = f; break; }
        }
      } else {
        var groups = scope.querySelectorAll('[data-qv-optgroup]');
        for (var g = 0; g < groups.length; g += 1) {
          var ddl = groups[g].querySelector('[data-qv-select]');
          if (ddl) { if (ddl.value === '') { target = ddl; break; } continue; }
          if (!groups[g].querySelector('[data-qv-opt][aria-pressed="true"]')) { target = groups[g]; break; }
        }
      }
      if (target) {
        if (typeof target.focus === 'function' && target.tagName !== 'DIV') {
          try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); }
        }
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, true);

  /* Diagnostic: jjQvDebug() — why is the quick view button gated? */
  window.jjQvDebug = function () {
    var scope = document.querySelector('[data-qv-panel]:not([hidden])') || document.querySelector('.jj-qv');
    if (!scope) return 'no quick view open';
    var btn = scope.querySelector('[data-qv-atc]');
    return {
      gated: btn ? btn.hasAttribute('data-qv-gated') : null,
      reason: btn ? btn.dataset.gateReason || null : null,
      label: btn ? btn.textContent.trim() : null,
      variantId: (scope.querySelector('[data-qv-variant-id]') || {}).value || null,
      options: Array.prototype.map.call(scope.querySelectorAll('[data-qv-optgroup]'), function (g) {
        var ddl = g.querySelector('[data-qv-select]');
        return {
          name: (g.querySelector('.jj-qv__optname') || {}).textContent.trim().slice(0, 30),
          type: ddl ? 'select' : 'chips',
          chosen: ddl ? ddl.value : ((g.querySelector('[data-qv-opt][aria-pressed="true"]') || {}).dataset || {}).qvValue || null
        };
      }),
      fields: Array.prototype.map.call(scope.querySelectorAll('[data-jj-cf-input]'), function (f) {
        return { name: f.name, value: f.value, required: f.dataset.jjCfRequired === 'true', min: f.dataset.jjCfMin || null, len: f.value.trim().length };
      })
    };
  };
})();

/* ---------- size-suggest loader ----------
   Symmetry's quick-buy modal injects product markup with innerHTML, and
   innerHTML never executes <script src>. On a product page that does not
   matter — the page itself loaded jj-samsung-size-suggest.js — but on a
   COLLECTION page the widget only ever exists inside the modal, so the script
   was never fetched: the widget rendered but nothing initialised it, so it sat
   wherever it was rendered instead of moving under the Watch model row, and
   the mandatory measurement never applied.

   jj-blocks.js is loaded on every page, so it can do the loading. The widget's
   wrapper carries its own asset URL (data-ss-asset), which keeps the CDN path
   correct without this file needing to be a .liquid asset. */
(function () {
  var requested = false;
  function ensureLoaded() {
    if (requested || window.jjSSInit) return;
    var wrap = document.querySelector('.jj-ss-wrap[data-ss-asset]');
    if (!wrap) return;
    requested = true;
    var sc = document.createElement('script');
    sc.src = wrap.dataset.ssAsset;
    sc.defer = true;
    document.head.appendChild(sc);
  }
  ensureLoaded();
  new MutationObserver(function () {
    // already loaded: its own bootstrap picks up new copies
    if (window.jjSSInit) return;
    ensureLoaded();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();